import React from 'react';
import clsx from 'clsx';
import { ListItem } from './ListItem.tsx';
import ArrowIcon from '../../assets/icons/small_chevron.svg?react';
import styles from './LanguageSelector.module.scss';
import EnglishLanguage from '../../assets/icons/english-language.svg';
import { Language, languages } from '../../application/localization/locales.ts';
import GermanLanguage from '../../assets/icons/german-language.svg';
import FrenchLanguage from '../../assets/icons/french-language.svg';
import SpanishLanguage from '../../assets/icons/spanish-language.svg';
import { useScopedTranslation } from '../../application/localization/useScopedTranslation.ts';
import { UserMenuCategory } from '../../pages/authorized/rootPage/RootPage.tsx';
import { useAnalytics } from 'helpers/analytics.ts';
import { tDynamic } from '../../helpers/tDynamic.ts';

type LanguageType = {
  image: string;
  name: string;
};

const languageData: Record<Language, LanguageType> = {
  [Language.en]: {
    image: EnglishLanguage,
    name: 'english',
  },
  [Language.de]: {
    image: GermanLanguage,
    name: 'deutsch',
  },
  [Language.fr]: {
    image: FrenchLanguage,
    name: 'french',
  },
  [Language.es]: {
    image: SpanishLanguage,
    name: 'spanish',
  },
};

const languagesArray = languages.map((lang) => {
  return {
    id: lang,
    image: languageData[lang].image,
    name: languageData[lang].name,
  };
});

type OwnProps = {
  isInMenu: boolean;
  isLanguageModalShown: boolean;
  toggleLanguageModal: () => void;
  changeLanguage: (language: Language) => void;
};

const LanguageSelector = (props: OwnProps) => {
  const {
    isInMenu,
    isLanguageModalShown,
    toggleLanguageModal,
    changeLanguage,
  } = props;
  const { trackEvent } = useAnalytics();

  const i18n = useScopedTranslation('Page.LanguageSelector');

  const currentLanguage = i18n.i18n.language as Language;

  return (
    <div
      data-test-id={'language-selector'}
      className={clsx(
        styles.languageContainer,
        styles.cursor,
        styles.userModalItem,
        !isInMenu && styles.defaultIcon,
      )}
      onClick={(e) => {
        toggleLanguageModal();
        e.stopPropagation();
      }}
    >
      <img
        className={clsx(isInMenu && styles.image)}
        src={languageData[currentLanguage]?.image ?? EnglishLanguage}
      />
      <div className={styles.relative}>
        {isLanguageModalShown && (
          <div
            className={clsx(
              styles.languageModalBody,
              !isInMenu && styles.defaultPosition,
            )}
          >
            {languagesArray.map((lang) => (
              <ListItem
                text={tDynamic(i18n, lang.name)}
                icon={lang.image}
                isSelected={lang.id === currentLanguage}
                itemKey={lang.id}
                key={lang.id}
                onClickHandler={() => {
                  trackEvent({
                    category: UserMenuCategory,
                    action: 'Change language',
                    name: lang.name.toString(),
                  });
                  changeLanguage(lang.id);
                }}
              />
            ))}
          </div>
        )}
      </div>
      {isInMenu && (
        <>
          <div>{i18n.t('language')}</div>
          <ArrowIcon className={clsx(styles.arrow, styles.marginLeft)} />
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
